// This file was separated out of resource-fields.ts to avoid circular import
// errors with beehive-api-service and cast-data-to-entity.ts

import { Type } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder } from '@angular/forms';
import { getInstanceOrClassMetadata } from '../../../metadata';
import { ApiResource } from '../../../../api/api-resource';
import { FieldData, FIELDS } from './resource-fields';

export function getFieldsArray(resource: unknown): FieldData[] {
  return Object.values(getFields(resource));
}

export function getFields<
  T extends unknown,
  K = T extends Type<infer K> ? K : T,
>(resource: T): { [k in keyof K]: FieldData } {
  return getInstanceOrClassMetadata(resource, FIELDS) ?? {};
}

export function getFieldsAsForm<
  T extends unknown,
  K = T extends Type<infer K> ? K : T,
>(resource: T, fb: NonNullableFormBuilder) {
  const group = fb.group(
    Object.fromEntries(
      Object.entries(getFields(resource))
        .filter(([key, fieldData]) => !fieldData.readonly)
        .map(([key, fieldData]) => {
          const formControlFactory = fieldData.formControlFactory;
          if (!formControlFactory) {
            return [
              key,
              [{ value: fieldData.default ?? undefined, disabled: false }],
            ];
          }
          // fixme: paradox for form arrays: the form control describes the single control in the array
          //  and somehow the array should know intrinsically how to add new ones; metadata? mutating
          //  the form array api? For now, we just create the single element because I vaguely remember
          //  changing it intentionally...
          return [key, formControlFactory(fb)];
        }),
    ) as { [k in keyof K]: AbstractControl<K[k]> },
  );
  return group;
}

export function getNestedFields(
  resource: Type<ApiResource<any>>,
  maxDepth: number,
) {
  const getSubFields = (resource: Type<ApiResource<any>>) =>
    getFieldsArray(resource).filter((f) => f.includeFilter ?? true);

  const fields = getSubFields(resource);
  let newFields = fields;
  let depth = 0;
  while (depth++ < maxDepth) {
    newFields = newFields.flatMap((f) =>
      getSubFields(f.type()).map((innerField) => ({
        ...innerField,
        key: `${f.key.toString()}.${innerField.key.toString()}`,
        label: `${f.label} > ${innerField.label}`,
      })),
    );
    fields.push(...newFields);
  }
  return fields;
}

export function getNestedFieldData(
  resource: Type<ApiResource<any>>,
  name: string,
): FieldData {
  let fields = getFields(resource);
  const paths = name.split('.');
  if (paths.length == 1) {
    if (!(name in fields)) {
      throw new Error(`Column '${name}' not found on ${resource.name}`);
    }
    return fields[name as keyof typeof fields];
  }
  let field!: FieldData;
  for (const [i, path] of paths.entries()) {
    if (!(path in fields)) {
      throw new Error(
        `Column '${path}' (part of ${name}) not found on ${resource.name}`,
      );
    } else {
      field = fields[path as keyof typeof fields];
      if (i == paths.length - 1) break;
      resource = field.type();
      fields = getFields(resource);
      if (fields === undefined) {
        throw new Error(
          `Can't recurse into column '${path}' (part of ${name})`,
        );
      }
    }
  }
  // Override the key so that derived filter data links to correct filter column
  return { ...field, key: name };
}
